import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

/**
 * The page shell: one place that owns a screen's max-width, gutters and vertical
 * rhythm.
 *
 * `Admin.jsx` deliberately provides none of this - `MainContent` and `Content`
 * set no max-width, no horizontal padding and no bottom padding, and their
 * `padding-top` is 0 on desktop (the 56px below 975px only clears the fixed
 * `MobileTopBar`). So every screen improvised, and they disagreed:
 *
 *   top padding above the heading   0px Profile / 20px Customers, Analytics /
 *                                   40px Bookings, Galleries, Packages, Blog,
 *                                   Pages / 76px Settings
 *   width                           1100px Profile / 98% Bookings / 85% Settings
 *                                   / 100% everything else
 *   horizontal gutters              8 distinct schemes, 16px to 80px
 *   bottom spacing                  7 distinct values, 0px to 225px
 *   breakpoints                     5 distinct, 576 to 975
 *
 * Profile's 0px is why its heading sat flush against the top of the viewport.
 *
 * The frame is now the CALENDAR TAB's: 40px top, 80px gutters, 80px bottom, with
 * Calendar's own two breakpoints replacing the five in play. Calendar was chosen
 * as the reference deliberately - it is the widest of the eight gutter schemes,
 * so aligning on it moves content outward rather than cramping any screen, and
 * the tabs already using it (Calendar, Galleries) do not move at all. Calendar
 * also sets no max-width, so the default cap here is `none` to match.
 *
 * The earlier defaults here were the modal values instead (32px gutters, 1100px
 * cap, centred). That looked reasonable per-screen and was wrong in aggregate:
 * because the cap was centred, it decided the left edge, so a capped screen began
 * 65px in, an uncapped one 32px, and Calendar 80px. Switching tabs slid the
 * content sideways every time.
 *
 * Deliberately absent:
 *
 * - **No `min-height`.** Three screens carry `calc(100vh - 90px)` or
 *   `calc(100vh - 16em)` against chrome that no longer exists. `MainContent` is
 *   already `height: 100vh` with its own scroll, so a `min-height` here just
 *   invents space.
 * - **No heading.** Compose with `PageHeader` - layout and heading semantics stay
 *   separate concerns, which is what lets a screen put actions in the header
 *   without the shell knowing about them.
 * - **No `em`, no viewport reflow.** House rules 1 and 6: the shell is dropped
 *   inside the `font-size: 10px` admin anchor, and its gutters are its own.
 */

/*
 * The gutter and the cap are now separate concerns, and that split is the point.
 *
 * These used to be centred with `margin: 0 auto`, which meant the cap DECIDED THE LEFT EDGE: a
 * screen capped at 1100px started 65px in from the content region, an uncapped one started at
 * 32px, and Calendar - which owns its own frame - started at 80px. Nine distinct left offsets
 * across the admin tabs, and switching tabs shifted the content sideways every time.
 *
 * Left-aligning instead means every screen starts at exactly one gutter regardless of its cap,
 * so a cap can still shorten a long form's line length without moving where the page begins.
 */
const MAX_WIDTHS = {
  sm: '720px',
  md: '1100px',
  lg: '1440px',
  full: 'none',
}

/*
 * The DEFAULT is `full`, i.e. no cap.
 *
 * It used to be `md`, and that is a second way the frame could disagree between tabs: a capped
 * screen's content stops short of an uncapped one's, so Profile, Settings and Domains ended their
 * content 1100px in while Calendar, Blog, Forms and the rest ran to the gutter. The left edges
 * matched and the right edges did not, which still reads as the content moving when you switch
 * tabs. Calendar - the reference - has no cap, so neither does the default.
 *
 * `size` stays available for a screen that genuinely wants a shorter measure, but it now has to
 * ask for it.
 */

const Page = ({ size = 'full', flush = false, children, className, id, ...rest }) => (
  <Root $size={size} $flush={flush} className={className} id={id} {...rest}>
    {children}
  </Root>
)

Page.propTypes = {
  /** Content cap. `full` is for grids, calendars and split-pane editors. */
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'full']),
  /** Drop the top padding, for a screen whose first child is a full-bleed banner. */
  flush: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  id: PropTypes.string,
}

export default Page

/**
 * The frame on its own, for screens that already have an outer container and cannot take another
 * wrapper without disturbing what is positioned against it.
 *
 * Exported so the gutter is defined ONCE. Several admin screens anchor absolutely-positioned
 * children - LockScreenDimmer, sticky action bars - to their existing outer div, and inserting a
 * `<Page>` above that changes what those children resolve against. Applying the frame to the
 * container they already have avoids that entirely while keeping a single source of truth:
 *
 *   import { pageFrame } from 'components/ui'
 *
 *   const Container = styled.div`
 *     ${pageFrame}
 *     // ...whatever else the screen needs
 *   `
 *
 * 40 / 80 / 80, taken from the Calendar tab. Calendar was the widest of the eight gutter schemes
 * in play, so aligning on it moves content outward rather than cramping any screen, and the tabs
 * already using it - Calendar, Galleries - do not move at all.
 *
 * Calendar's own two breakpoints come along too, so the responsive behaviour matches and not just
 * the desktop inset. Its `width: 90%` at 767px is deliberately NOT copied: combined with the
 * centring on its parent that inset the content a further 5% either side, which is a quirk rather
 * than something to standardise on.
 */
export const pageFrame = css`
  width: 100%;
  padding: var(--space-10) var(--space-20) var(--space-20);

  @media (max-width: 767px) {
    padding: var(--space-10) var(--space-5) var(--space-8);
  }
  @media (max-width: 500px) {
    padding: var(--space-5) var(--space-3) var(--space-6);
  }
`

const Root = styled.div`
  ${pageFrame}
  max-width: ${({ $size }) => MAX_WIDTHS[$size] || MAX_WIDTHS.md};
  /* Left-aligned, NOT margin: 0 auto - see the note on MAX_WIDTHS. */
  margin: 0;

  /* Declared after the frame so it wins. */
  ${({ $flush }) =>
    $flush &&
    css`
      padding-top: 0;

      @media (max-width: 767px) {
        padding-top: 0;
      }
      @media (max-width: 500px) {
        padding-top: 0;
      }
    `}
`
